import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Settings as SettingsIcon, PenLine, FileUp, Cloud, FileText } from "lucide-react";
import { countWords } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useScripts, Script, ScriptSettings } from "@/hooks/useScripts";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useScriptLimits } from "@/hooks/useScriptLimits";
import { useToast } from "@/hooks/use-toast";
import { useOfflineDetection } from "@/hooks/useOfflineDetection";
import { useOfflineStorage } from "@/hooks/useOfflineStorage";
import { useSubscription } from "@/hooks/useSubscription";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { CloudImportButtons } from "@/components/editor/CloudImportButtons";
import { CloudFilePicker } from "@/components/editor/CloudFilePicker";
import { FileUploadZone } from "@/components/editor/FileUploadZone";
import { EditorHeader } from "@/components/editor/EditorHeader";
import { EditorMobileNav } from "@/components/editor/EditorMobileNav";
import { EditorSettingsBar } from "@/components/editor/EditorSettingsBar";
import { EditorPreviewPanel } from "@/components/editor/EditorPreviewPanel";
import { SubscriptionRequiredModal } from "@/components/modals/SubscriptionRequiredModal";

const defaultSettings: ScriptSettings = {
  fontSize: 48,
  fontColor: "#ffffff",
  bgColor: "#1a1a1a",
  scrollSpeed: 50,
  mirrorMode: false,
};

export default function Editor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { getScript, updateScript, createScript } = useScripts();
  const { 
    getCharacterLimit, 
    isApproachingCharacterLimit, 
    isOverCharacterLimit,
    getCharacterUsagePercentage,
    formatLimit,
    canUseMirrorMode,
  } = useScriptLimits();
  const { isOffline } = useOfflineDetection();
  const { cacheScript, isScriptCached, getCachedScript } = useOfflineStorage();
  const { isPro } = useSubscription();
  const { isTrialExpired } = useTrialStatus();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  const [script, setScript] = useState<Script | null>(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [sceneType, setSceneType] = useState("recording");
  const [settings, setSettings] = useState<ScriptSettings>(defaultSettings);
  const [googlePickerOpen, setGooglePickerOpen] = useState(false);
  const [dropboxPickerOpen, setDropboxPickerOpen] = useState(false);
  
  // No longer need justCreatedIdRef as we use location.state
  
  // Load or create script (support offline)
  useEffect(() => {
    const loadScript = async () => {
      if (id === "new") {
        if (isOffline) {
          toast({
            title: t("offline.offlineMode"),
            description: t("offline.cannotCreateOffline", "Cannot create scripts while offline"),
            variant: "destructive",
          });
          navigate("/dashboard");
          return;
        }
        
        try {
          const newScript = await createScript("recording");
          
          if (newScript && newScript.id) {
            // Navigate with state to preserve the script object across the route change
            navigate(`/editor/${newScript.id}`, { 
              replace: true,
              state: { script: newScript }
            });
            // We return here because navigate will unmount/remount or at least handle the new ID
            return;
          } else {
            toast({
              title: t("settings.error", "Error"),
              description: t("editor.errors.createFailed", "Failed to create script"),
              variant: "destructive",
            });
            navigate("/dashboard");
          }
        } catch (err) {
          console.error("Create script error:", err);
          toast({
            title: t("settings.error", "Error"),
            description: err instanceof Error ? err.message : t("editor.errors.createFailed", "Failed to create script"),
            variant: "destructive",
          });
          navigate("/dashboard");
        }
        return;
      }

      if (id) {
        // Check if we have the script passed via navigation state
        // This avoids double-fetching and race conditions logic
        const stateScript = (location.state as { script?: Script } | null)?.script;
        if (stateScript && stateScript.id === id) {
           setScript(stateScript);
           setTitle(stateScript.title);
           setContent(stateScript.content);
           setSceneType(stateScript.scene_type);
           setSettings(stateScript.settings);
           
           // Clear state so reload fetches fresh
           window.history.replaceState({}, document.title);
           
           setLoading(false);
           return;
        }

        // Just matching ID in current state is also good optimization
        if (script && script.id === id) {
             setLoading(false);
             return;
        }

        // Try online first, fall back to cached
        if (isOffline) {
          const cached = await getCachedScript(id);
          if (cached) {
            setScript({
              id: cached.id,
              user_id: '',
              title: cached.title,
              content: cached.content,
              scene_type: cached.scene_type,
              word_count: cached.word_count,
              settings: cached.settings as ScriptSettings,
              updated_at: cached.cachedAt.toISOString(),
              created_at: cached.cachedAt.toISOString(),
            });
            setTitle(cached.title);
            setContent(cached.content);
            setSceneType(cached.scene_type);
            setSettings(cached.settings as ScriptSettings);
          } else {
            toast({
              title: t("offline.offlineMode"),
              description: t("offline.scriptNotCached", "This script is not available offline"),
              variant: "destructive",
            });
            navigate("/dashboard");
          }
        } else {
          // If we already have the script in state and IDs match, don't re-fetch
          if (script && script.id === id) {
             console.log('[Editor] Script already loaded in state, skipping fetch');
             setLoading(false);
             return;
          }

          try {
            const data = await getScript(id);
            if (data && data.id) {
              setScript(data);
              setTitle(data.title);
              setContent(data.content);
              setSceneType(data.scene_type);
              setSettings(data.settings);
            } else {
              console.error('[Editor] getScript returned null or invalid data');
              toast({
                title: t("settings.error"),
                description: t("editor.errors.loadFailed", "Failed to load script"),
                variant: "destructive"
              });
              navigate("/dashboard");
            }
          } catch (error) {
            console.error('[Editor] Failed to load script:', error);
             toast({
                title: t("settings.error"),
                description: t("editor.errors.loadFailed", "Failed to load script"),
                variant: "destructive"
              });
            navigate("/dashboard");
          }
        }
      }
      setLoading(false);
    };

    loadScript();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isOffline]);

  // Handle caching script for offline
  const handleCacheScript = async () => {
    if (!script || !isPro) return;
    
    const success = await cacheScript({
      id: script.id,
      title,
      content,
      settings,
      scene_type: sceneType,
      word_count: countWords(content)
    });

    if (success) {
      toast({
        title: t("offline.cached"),
        description: t("offline.scriptCachedDesc", "Script saved for offline use")
      });
    } else {
      toast({
        title: t("settings.error"),
        description: t("offline.maxScriptsReached"),
        variant: "destructive"
      });
    }
  };

  // Auto-save
  const saveData = useCallback(async (data: { 
    id?: string, 
    title: string, 
    content: string, 
    sceneType: string, 
    settings: ScriptSettings 
  }) => {
    if (!data.id) {
      return false;
    }
    return await updateScript(data.id, {
      title: data.title,
      content: data.content,
      scene_type: data.sceneType,
      settings: data.settings,
    });
  }, [updateScript]);

  const { status, forceSave, hasUnsavedChanges } = useAutoSave(
    { id: script?.id, title, content, sceneType, settings },
    saveData,
    2000
  );

  // Warn on navigation with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Character count
  const characterCount = content.length;
  const wordCount = countWords(content);
  const charLimit = getCharacterLimit();
  const charUsage = getCharacterUsagePercentage(characterCount);
  const isApproaching = isApproachingCharacterLimit(characterCount);
  const isOver = isOverCharacterLimit(characterCount);

  const handleContentChange = (value: string) => {
    if (isOver && value.length > content.length) {
      toast({
        title: t("editor.errors.limitReached"),
        description: t("editor.errors.upgradeForMore"),
        variant: "destructive",
      });
      return;
    }
    setContent(value);
  };

  const handlePlay = async () => {
    // Check if trial expired for free users
    if (isTrialExpired) {
      setShowSubscriptionModal(true);
      return;
    }
    
    if (hasUnsavedChanges) {
      await forceSave();
    }
    navigate(`/teleprompter/${script?.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <EditorHeader
        title={title}
        setTitle={setTitle}
        sceneType={sceneType}
        setSceneType={setSceneType}
        status={status}
        isOffline={isOffline}
        isPro={isPro}
        script={script}
        isScriptCached={isScriptCached}
        handleCacheScript={handleCacheScript}
        forceSave={forceSave}
        handlePlay={handlePlay}
        wordCount={wordCount}
        characterCount={characterCount}
        charLimit={charLimit}
        charUsage={charUsage}
        isApproaching={isApproaching}
        isOver={isOver}
        formatLimit={formatLimit}
      />

      {/* Main Editor */}
      <div className="container mx-auto px-4 pt-4 md:pt-24 pb-24 md:pb-8">
        {/* Settings Bar - Horizontal above editor (desktop only) */}
        <div className="hidden lg:block">
          <EditorSettingsBar
            settings={settings}
            setSettings={setSettings}
            canUseMirrorMode={canUseMirrorMode}
          />
        </div>

        <div className="flex gap-6">
          {/* Editor Area */}
          <div className="flex-1 min-w-0">
            <Tabs defaultValue="direct" className="w-full">
              <TabsList className="w-full grid grid-cols-3 h-12 p-0 bg-transparent border-2 border-black rounded-xl overflow-hidden shadow-[4px_4px_0_#1A1A1A]">
                <TabsTrigger 
                  value="direct" 
                  className="rounded-none border-r-2 border-black data-[state=active]:bg-[#FFD93D] data-[state=active]:text-black data-[state=active]:shadow-none bg-white hover:bg-gray-50 h-full flex items-center justify-center gap-2"
                >
                  <PenLine className="h-4 w-4 md:hidden" />
                  <span className="hidden md:inline">{t("editor.tabs.directInput")}</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="upload" 
                  className="rounded-none border-r-2 border-black data-[state=active]:bg-[#FFD93D] data-[state=active]:text-black data-[state=active]:shadow-none bg-white hover:bg-gray-50 h-full flex items-center justify-center gap-2"
                >
                  <FileUp className="h-4 w-4 md:hidden" />
                  <span className="hidden md:inline">{t("editor.tabs.fileUpload")}</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="cloud" 
                  className="rounded-none data-[state=active]:bg-[#FFD93D] data-[state=active]:text-black data-[state=active]:shadow-none bg-white hover:bg-gray-50 h-full flex items-center justify-center gap-2"
                >
                  <Cloud className="h-4 w-4 md:hidden" />
                  <span className="hidden md:inline">{t("editor.tabs.cloud")}</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="direct" className="mt-4">
                <Textarea
                  value={content}
                  onChange={(e) => handleContentChange(e.target.value)}
                  placeholder={t("editor.placeholder")}
                  className="h-[62vh] lg:min-h-[500px] text-lg resize-none border-2 border-black rounded-xl bg-white focus-visible:ring-2 focus-visible:ring-[#FF6B9D] focus-visible:ring-offset-0 p-4 leading-relaxed shadow-[4px_4px_0_#1A1A1A]"
                />
              </TabsContent>

              <TabsContent value="upload" className="mt-4">
                <FileUploadZone onImport={(importedContent) => setContent(prev => prev ? prev + "\n" + importedContent : importedContent)} />
              </TabsContent>

              <TabsContent value="cloud" className="mt-4">
                <CloudImportButtons 
                  onGoogleClick={() => setGooglePickerOpen(true)}
                  onDropboxClick={() => setDropboxPickerOpen(true)}
                />
              </TabsContent>
            </Tabs>

            {/* Cloud File Pickers */}
            <CloudFilePicker
              provider="google"
              open={googlePickerOpen}
              onOpenChange={setGooglePickerOpen}
              onImport={(importedContent) => setContent(prev => prev + "\n" + importedContent)}
            />
            <CloudFilePicker
              provider="dropbox"
              open={dropboxPickerOpen}
              onOpenChange={setDropboxPickerOpen}
              onImport={(importedContent) => setContent(prev => prev + "\n" + importedContent)}
            />
          </div>

          {/* Preview Panel - Right sidebar (desktop only) */}
          <div className="hidden lg:block w-80 xl:w-[360px] shrink-0">
            <EditorPreviewPanel
              settings={settings}
              content={content}
            />
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation & Settings */}
      <Sheet>
        <EditorMobileNav
          onPlay={handlePlay}
          settingsTrigger={
            <SheetTrigger asChild>
              <Button
                className="
                  w-full h-12 text-base font-bold
                  bg-white text-black border-2 border-black rounded-lg
                  hover:bg-gray-50 hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[4px_4px_0_#1A1A1A]
                  active:translate-x-1 active:translate-y-1 active:shadow-none
                  transition-all duration-100
                "
              >
                <SettingsIcon className="h-5 w-5 mr-2" />
                {t("editor.settings.title")}
              </Button>
            </SheetTrigger>
          }
        />
        <SheetContent side="left">
          <SheetHeader>
            <SheetTitle>{t("editor.settings.title")}</SheetTitle>
            <SheetDescription className="sr-only">
              Editor settings and preview controls
            </SheetDescription>
          </SheetHeader>
          <div className="mt-2 space-y-4">
            {/* Mobile: Vertical settings layout */}
            <EditorSettingsBar
              settings={settings}
              setSettings={setSettings}
              canUseMirrorMode={canUseMirrorMode}
            />
            <EditorPreviewPanel
              settings={settings}
              content={content}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Subscription Required Modal */}
      <SubscriptionRequiredModal 
        isOpen={showSubscriptionModal} 
        onClose={() => setShowSubscriptionModal(false)} 
      />
    </div>
  );
}
